import { createMachine } from 'xstate';
import { pure } from 'xstate/lib/actions';

import { OutstandingAgreementType } from '__generated__/GQL';
import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';
import { ApplicationType } from 'features/Onboarding/types';

import locales from '../../../locales';
import actions, { Action } from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';
import { State } from './state';

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'capital-deposit',

    initial: State.INIT,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.INIT]: {
        always: [
          /**
           * Checkpoints
           */
          {
            cond: Guard.IsAwaitingAccountValidation,
            target: State.APPLICATION_FOLLOW_UP,
          },
          {
            cond: Guard.IsApplicationProfileComplete,
            target: State.VERIFICATION,
          },

          /**
           * Chronological steps
           */
          {
            cond: Guard.shouldFillPersonalInfo,
            target: State.CAPITAL_DEPOSIT_GUIDE,
          },
          {
            cond: Guard.ShouldFillCompanyComplianceQuestions,
            target: State.CAPITAL_DEPOSIT_EU_BANK_ACCOUNT,
          },
          {
            cond: Guard.ShouldFillCapitalDepositQuestions,
            target: State.CAPITAL_DEPOSIT_QUESTIONS,
          },
          {
            cond: Guard.ShouldRegisterBeneficiaries,
            target: State.BENEFICIARIES,
          },

          /**
           * Safeguard that should never trigger.
           */
          {
            actions: Action.LogUnexpectedState,
            target: State.CAPITAL_DEPOSIT_GUIDE,
          },
        ],
      },

      [State.CAPITAL_DEPOSIT_GUIDE]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.PERSONAL_INFO,
          PREV: State.ABORT_APPLICATION,
        },
      },

      [State.PERSONAL_INFO]: {
        on: {
          NEXT: State.CAPITAL_DEPOSIT_EU_BANK_ACCOUNT,
          PREV: State.CAPITAL_DEPOSIT_GUIDE,
        },
      },

      [State.CAPITAL_DEPOSIT_EU_BANK_ACCOUNT]: {
        on: {
          NEXT: State.COMPANY_COMPLIANCE,
          PREV: State.PERSONAL_INFO,
        },
      },

      [State.COMPANY_COMPLIANCE]: {
        on: {
          NEXT: State.CAPITAL_DEPOSIT_QUESTIONS,
          PREV: State.CAPITAL_DEPOSIT_EU_BANK_ACCOUNT,
        },
      },

      [State.CAPITAL_DEPOSIT_QUESTIONS]: {
        on: {
          NEXT: State.BENEFICIARIES,
          PREV: State.COMPANY_COMPLIANCE,
        },
      },

      [State.BENEFICIARIES]: {
        on: {
          NEXT: State.USER_PROFILE_SEND,
          PREV: State.CAPITAL_DEPOSIT_QUESTIONS,
        },
      },

      [State.USER_PROFILE_SEND]: {
        invoke: {
          onDone: {
            target: State.VERIFICATION,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.INIT,
          },
          src: Service.FinishProfileSetup,
        },
      },

      [State.VERIFICATION]: {
        always: {
          cond: Guard.IsAwaitingAccountValidation,
          target: State.APPLICATION_FOLLOW_UP,
        },
        on: {
          NEXT: State.CAPITAL_DEPOSIT_PLAN_CHOICE,
          PREV: {
            actions: shared.actions.toast({
              status: 'error',
              title: locales.toast.definitiveStep,
            }),
          },
        },
      },

      [State.CAPITAL_DEPOSIT_AGREEMENT]: {
        always: {
          cond: {
            type: Guard.HasCompletedAgreementOfType,
            value: OutstandingAgreementType.NotaryFourez,
          },
          target: State.USER_AGREEMENT,
        },

        on: {
          NEXT: State.USER_AGREEMENT,
          PREV: State.VERIFICATION,
        },
      },

      [State.USER_AGREEMENT]: {
        always: {
          cond: {
            type: Guard.HasCompletedAgreementOfType,
            value: OutstandingAgreementType.Cgu,
          },
          target: State.CAPITAL_DEPOSIT_SUBMIT_APPLICATION,
        },

        on: {
          NEXT: State.CAPITAL_DEPOSIT_SUBMIT_APPLICATION,
          PREV: State.VERIFICATION,
        },
      },

      [State.CAPITAL_DEPOSIT_PLAN_CHOICE]: {
        /**
         * In this case, the Capital Deposit is paid, but the companyUser is not "awaiting account validation".
         * Therefore we need to submit the application (eg. after a KYB/KYC correction from the user).
         */
        always: {
          cond: Guard.IsCapitalDepositPaid,
          target: State.CAPITAL_DEPOSIT_AGREEMENT,
        },

        on: {
          NEXT: State.CAPITAL_DEPOSIT_AGREEMENT,
          PREV: State.VERIFICATION,
        },
      },

      [State.CAPITAL_DEPOSIT_SUBMIT_APPLICATION]: {
        invoke: {
          onDone: {
            actions: [
              pure(({ getModuleData }) =>
                shared.actions.track({
                  name: 'Customer Acquisition Completed',
                  properties: {
                    accountType: ApplicationType.CapitalDeposit,
                    plan: getModuleData().viewer.company?.metadata
                      .selectedPlanId,
                  },
                }),
              ),

              shared.actions.track({
                name: 'submitApplication',
                properties: {
                  isCapitalDeposit: true,
                  isPrimaryApplicant: true,
                  isShineStart: false,
                  proofOfAddressSentByEmail: false,
                },
              }),
            ],
            target: State.APPLICATION_FOLLOW_UP,
          },

          onError: {
            actions: shared.actions.error(),
            target: State.VERIFICATION,
          },

          src: Service.SubmitApplication,
        },
      },

      [State.DOWNLOAD_SHINE_MESSAGE_SENT]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.CAPITAL_DEPOSIT_NEXT_STEPS,
        },
      },

      [State.CAPITAL_DEPOSIT_NEXT_STEPS]: {
        entry: [
          shared.actions.trackView(),
          Action.TriggerOnboardingCompletedMessage,
        ],

        on: {
          DOWNLOAD_SHINE: State.DOWNLOAD_SHINE_MESSAGE_SENT,
          GO_BENEFICIARIES_DOCS: State.BENEFICIARIES_DOCS,
          PREV: {
            actions: shared.actions.toast({
              status: 'error',
              title: locales.toast.definitiveStep,
            }),
          },
        },
      },

      [State.APPLICATION_FOLLOW_UP]: {
        always: {
          cond: Guard.IsAwaitingBeneficiaries,
          target: State.BENEFICIARIES_DOCS,
        },
      },

      [State.BENEFICIARIES_DOCS]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.CAPITAL_DEPOSIT_NEXT_STEPS,
        },
      },

      [State.ABORT_APPLICATION]: {
        invoke: {
          onDone: {
            target: State.DONE,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.INIT,
          },
          src: Service.AbortApplication,
        },
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
